import { Children, ReactNode, isValidElement, PropsWithChildren } from 'react';

import { ChartType, ChartBlocksSummary } from '../../types';
import { Area } from '../area';
import { Bar } from '../bar';
import { Line } from '../line';
import { Scatter } from '../scatter';

const readChartChildren = (
  nodes: ReactNode,
  count: ChartBlocksSummary
): void => {
  Children.forEach(nodes, (child) => {
    if (!isValidElement<PropsWithChildren>(child)) {
      return;
    }

    switch (child.type) {
      case Area:
        count.area++;
        break;
      case Bar:
        count.bar++;
        break;
      case Line:
        count.line++;
        break;
      case Scatter:
        count.scatter++;
        break;
    }

    if (child.props.children) {
      readChartChildren(child.props.children, count);
    }
  });
};

export const determineChartType = (
  children: ReactNode,
  fallbackChartType: ChartType,
  zoomEnabled = false
): { chartBlocks: ChartBlocksSummary; chartType: ChartType } => {
  const summary: ChartBlocksSummary = {
    area: 0,
    bar: 0,
    line: 0,
    scatter: 0,
  };

  readChartChildren(children, summary);

  const activeTypes = Object.entries(summary)
    .filter(([_, value]) => value > 0)
    .map(([key]) => key as ChartType);

  if (activeTypes.length > 1) {
    return {
      chartBlocks: summary,
      chartType: 'composed',
    };
  }

  if (activeTypes.length === 1) {
    // ScatterChart doesn't populate activeLabel in mouse events, breaking drag-to-zoom.
    // Scatter works fine inside ComposedChart, so upgrade when zoom is enabled.
    const chartType =
      zoomEnabled && activeTypes[0] === 'scatter' ? 'composed' : activeTypes[0];
    return { chartBlocks: summary, chartType };
  }

  // eslint-disable-next-line no-console
  console.warn(`Unable to determine chartType, using "${fallbackChartType}".`);
  return {
    chartBlocks: summary,
    chartType: fallbackChartType,
  };
};
